import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../../components/ui/button';
import { Card, CardContent } from '../../components/ui/card';
import { Input } from '../../components/ui/input';
import { 
  ChevronLeft, Camera, Loader2, CheckCircle, AlertCircle,
  Euro, Calendar, FileText, Fuel, Car, ParkingCircle, 
  Wrench, Shield, MoreHorizontal, Sparkles, Upload
} from 'lucide-react';

const CATEGORIES = [
  { id: 'fuel', name: 'Carburant', icon: Fuel, color: '#F59E0B' },
  { id: 'toll', name: 'Péages', icon: Car, color: '#3B82F6' },
  { id: 'parking', name: 'Parking', icon: ParkingCircle, color: '#8B5CF6' },
  { id: 'maintenance', name: 'Entretien', icon: Wrench, color: '#EF4444' },
  { id: 'insurance', name: 'Assurance', icon: Shield, color: '#10B981' },
  { id: 'other', name: 'Autre', icon: MoreHorizontal, color: '#6B7280' }
];

const NewExpensePage = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState('');
  const [mounted, setMounted] = useState(false);
  const [formData, setFormData] = useState({
    amount: '',
    category: 'fuel',
    description: '',
    date: new Date().toISOString().split('T')[0],
    vehicle_id: ''
  });
  const [vehicles, setVehicles] = useState([]);

  const API_URL = process.env.REACT_APP_BACKEND_URL;

  useEffect(() => {
    setMounted(true);
    fetchVehicles();
  }, []);

  const fetchVehicles = async () => {
    const token = localStorage.getItem('optitrajet_token');
    try {
      const res = await fetch(`${API_URL}/api/optitrajet/fleet/vehicles`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (res.ok) {
        const data = await res.json();
        setVehicles(data.vehicles || []);
      }
    } catch (err) {
      console.error('Fetch vehicles error:', err);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    const token = localStorage.getItem('optitrajet_token');
    
    try {
      const response = await fetch(`${API_URL}/api/optitrajet/expenses`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          ...formData,
          amount: parseFloat(formData.amount) || 0
        })
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.detail || 'Erreur lors de la création');
      }

      setSuccess(true);
      setTimeout(() => {
        navigate('/optitrajet/expenses');
      }, 1500);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  if (success) {
    return (
      <div className="min-h-screen flex items-center justify-center p-4" style={{ background: '#F8FAFC' }}>
        <Card className="w-full max-w-md border-0 shadow-xl">
          <CardContent className="p-8 text-center">
            <div className="w-16 h-16 rounded-full bg-green-100 flex items-center justify-center mx-auto mb-4 animate-in zoom-in duration-300">
              <CheckCircle className="h-8 w-8 text-green-600" />
            </div>
            <h2 style={{ fontFamily: "'Outfit', sans-serif" }} className="text-xl font-bold text-slate-800 mb-2">
              Dépense enregistrée !
            </h2>
            <p className="text-slate-500">Redirection en cours...</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen pb-8" style={{ background: '#F8FAFC', fontFamily: "'Inter', sans-serif" }}>
      {/* Header */}
      <header className="sticky top-0 z-50 bg-white/80 backdrop-blur-xl border-b border-slate-200">
        <div className="max-w-2xl mx-auto px-4 h-16 flex items-center">
          <button 
            onClick={() => navigate('/optitrajet/expenses')}
            className="p-2 rounded-lg hover:bg-slate-100 transition-colors"
          >
            <ChevronLeft className="h-5 w-5 text-slate-600" />
          </button>
          <h1 style={{ fontFamily: "'Outfit', sans-serif" }} className="text-xl font-bold text-slate-800 ml-2">
            Nouvelle dépense
          </h1>
        </div>
      </header>

      <main className="max-w-2xl mx-auto px-4 py-6">
        {/* Error */}
        {error && (
          <div className="mb-6 p-4 rounded-xl bg-red-50 border border-red-100 flex items-center gap-3">
            <AlertCircle className="h-5 w-5 text-red-500" />
            <p className="text-red-700 text-sm">{error}</p>
          </div>
        )}

        <Card className={`border-0 shadow-lg transition-all duration-500 ${mounted ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'}`}>
          <CardContent className="p-6">
            <form onSubmit={handleSubmit} className="space-y-6">
              {/* Amount */}
              <div>
                <label className="text-sm font-medium text-slate-700 mb-1.5 block">Montant</label>
                <div className="relative">
                  <Euro className="absolute left-4 top-1/2 -translate-y-1/2 h-5 w-5 text-slate-400" />
                  <Input
                    type="number"
                    step="0.01"
                    placeholder="0.00"
                    value={formData.amount}
                    onChange={(e) => setFormData({ ...formData, amount: e.target.value })}
                    className="pl-12 h-14 rounded-xl border-slate-200 text-2xl font-bold"
                    required
                    data-testid="expense-amount"
                  />
                </div>
              </div>

              {/* Category */}
              <div>
                <label className="text-sm font-medium text-slate-700 mb-3 block">Catégorie</label>
                <div className="grid grid-cols-3 gap-2">
                  {CATEGORIES.map((cat) => {
                    const Icon = cat.icon;
                    const isSelected = formData.category === cat.id;
                    return (
                      <button
                        key={cat.id}
                        type="button"
                        onClick={() => setFormData({ ...formData, category: cat.id })}
                        className={`p-3 rounded-xl border-2 transition-all ${
                          isSelected 
                            ? 'border-blue-500 bg-blue-50' 
                            : 'border-slate-200 hover:border-slate-300'
                        }`}
                        data-testid={`category-${cat.id}`}
                      >
                        <Icon 
                          className="h-6 w-6 mx-auto mb-1" 
                          style={{ color: isSelected ? cat.color : '#94a3b8' }}
                        />
                        <p className={`text-xs font-medium ${isSelected ? 'text-slate-800' : 'text-slate-500'}`}>
                          {cat.name}
                        </p>
                      </button>
                    );
                  })}
                </div>
              </div>

              {/* Date */}
              <div>
                <label className="text-sm font-medium text-slate-700 mb-1.5 block">Date</label>
                <div className="relative">
                  <Calendar className="absolute left-4 top-1/2 -translate-y-1/2 h-5 w-5 text-slate-400" />
                  <Input
                    type="date"
                    value={formData.date}
                    onChange={(e) => setFormData({ ...formData, date: e.target.value })}
                    className="pl-12 h-12 rounded-xl border-slate-200"
                    data-testid="expense-date"
                  />
                </div>
              </div>

              {/* Description */}
              <div>
                <label className="text-sm font-medium text-slate-700 mb-1.5 block">Description</label>
                <div className="relative">
                  <FileText className="absolute left-4 top-4 h-5 w-5 text-slate-400" />
                  <textarea
                    placeholder="Ex: Plein essence Total"
                    value={formData.description}
                    onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                    className="w-full pl-12 p-4 rounded-xl border border-slate-200 resize-none h-24 focus:border-blue-500 focus:ring-1 focus:ring-blue-500"
                    data-testid="expense-description"
                  />
                </div>
              </div>

              {/* Vehicle */}
              {vehicles.length > 0 && (
                <div>
                  <label className="text-sm font-medium text-slate-700 mb-1.5 block">Véhicule (optionnel)</label>
                  <select
                    value={formData.vehicle_id}
                    onChange={(e) => setFormData({ ...formData, vehicle_id: e.target.value })}
                    className="w-full h-12 px-4 rounded-xl border border-slate-200 bg-white focus:border-blue-500"
                  >
                    <option value="">Aucun véhicule</option>
                    {vehicles.map(v => (
                      <option key={v.id} value={v.id}>{v.name} ({v.plate_number})</option>
                    ))}
                  </select>
                </div>
              )}

              {/* Submit */}
              <Button
                type="submit"
                disabled={loading}
                className="w-full h-14 rounded-xl bg-gradient-to-r from-amber-500 to-orange-500 hover:from-amber-600 hover:to-orange-600 text-white font-semibold shadow-lg transition-all"
                data-testid="save-expense-btn"
              >
                {loading ? (
                  <Loader2 className="h-5 w-5 animate-spin" />
                ) : (
                  <>
                    <CheckCircle className="h-5 w-5 mr-2" />
                    Enregistrer
                  </>
                )}
              </Button>
            </form>
          </CardContent>
        </Card>
      </main>
    </div>
  );
};

export default NewExpensePage;
